import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.addEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }

    this.#openFromRedirect();
  }

  /**
   * Opens the drawer for the `/cart` -> `/?viewcart=true` bounce in layout/theme.liquid, which is
   * how a hosted-account "Buy again" lands the buyer in the drawer rather than on a cart page.
   * The param is stripped afterwards so a reload or a shared link does not reopen it.
   */
  #openFromRedirect() {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('viewcart')) return;

    params.delete('viewcart');
    const query = params.toString();
    history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}`);

    this.#themeDrawer?.open();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.removeEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
  }

  /**
   * Recomputes the sticky summary after a back/forward cache restore re-renders the cart items.
   *
   * On viewports at or above 1024px an open drawer survives a navigation and is reopened during
   * parsing (see the restore script in `snippets/theme-drawer.liquid`), so no open event fires
   * on the way back and the measurement taken before the shopper left is the one still applied.
   * An empty drawer measured as unpinned would keep the checkout CTA scrolling away once the
   * restore fills it with line items.
   */
  #handleCartSectionRestored = () => {
    requestAnimationFrame(() => this.#updateStickyState());
  };

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    // GOAT-OVERRIDE(installments-leak): bail when the element was never registered. On a store with
    // no installments provider `whenDefined` never settles, so every drawer open leaked a pending
    // promise holding this closure (and `this`) for the life of the page. Shopify registers the
    // element from its own script long before a shopper can open the drawer, so when installments
    // ARE available the behaviour is unchanged.
    if (!customElements.get('shopify-payment-terms')) return;

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    /* GOAT-OVERRIDE(instant-open): re-apply after every Horizon upgrade.
     *
     * Upstream gates this synchronous open on `!this.#isCartEmpty()`, which reads the drawer's
     * server-rendered markup — still the EMPTY state during the first add — so the very first
     * add only opened the drawer once the whole add + sections round-trip resolved (seconds on
     * the dev proxy). An `action === 'add'` cannot leave the cart empty, so open right away;
     * `data-cart-adding` veils the stale empty state until the content lands. The veil clears
     * when the drawer's markup actually stops being empty (MutationObserver) — NOT on the
     * event promise, which resolves before the content morph lands and would re-expose the
     * empty state for a few frames. 8s fail-open timeout: a failed add leaves the cart truly
     * empty, so the observer never fires and the veil must lift on its own. */
    if (shouldAutoOpen && !sourceModal) {
      if (this.#isCartEmpty()) {
        this.setAttribute('data-cart-adding', '');
        const clearVeil = () => {
          this.removeAttribute('data-cart-adding');
          observer.disconnect();
          clearTimeout(failOpen);
        };
        const observer = new MutationObserver(() => {
          if (!this.#isCartEmpty()) clearVeil();
        });
        observer.observe(this, { childList: true, subtree: true });
        const failOpen = setTimeout(clearVeil, 8000);
      }
      this.#themeDrawer?.open();
    }

    const settle = () => requestAnimationFrame(() => this.#updateStickyState());

    const openAndSettle = () => {
      if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
      settle();
    };

    // GOAT-OVERRIDE(auto-open): re-apply after every Horizon upgrade.
    //
    // Upstream writes `event.promise?.then(...).catch(...)`. The optional chain only guards
    // `.then`, so an announcement dispatched WITHOUT a promise evaluates to
    // `undefined.catch(...)` and throws a TypeError out of this listener. That matters because the
    // promise branch is the only path that opens the drawer when the guard above declined to:
    // `#isCartEmpty()` reads the drawer's markup, which is still the server-rendered EMPTY state
    // on the first add into an empty cart, so the synchronous open is skipped and the whole
    // auto-open depends on the promise resolving. Net effect of either a missing or a rejected
    // promise: the line item lands in the cart (the /cart/add.js request already succeeded) but
    // the drawer never opens.
    //
    // Any dispatcher that isn't Horizon's own can land here without a promise — most notably
    // `Shopify.actions.updateCart` from a third-party app (Swym, free-gift, subscription widgets),
    // which this theme points at the cart drawer in assets/standard-actions-override.js.
    const promise = event.promise;

    if (!promise) {
      if (shouldAutoOpen && !sourceModal) {
        openAndSettle();
      } else {
        settle();
      }
      return;
    }

    promise
      .then(({ detail }) => {
        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
        // Still settle the sticky-summary measurement — a rejected announcement doesn't mean the
        // cart is unchanged, and leaving `cart-summary-sticky` stale mis-sizes the drawer.
        settle();
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
